import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  useSlip,
  useAssignBoat,
  useVacateSlip,
  useDeleteSlip,
  useAllocations,
  useUpdateAllocationRate,
  useUpdateAllocationPayer,
} from '@/features/storage/hooks'
import { SlipFormDialog } from '@/features/storage/slip-form-dialog'
import { useBoats } from '@/features/inventory/hooks'
import { useCustomers } from '@/features/customers/hooks'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { PriceInput } from '@/components/ui/price-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Anchor,
  Ruler,
  Ship,
  ExternalLink,
  Pencil,
  UserPlus,
  UserMinus,
  Trash2,
} from 'lucide-react'
import { formatMoney } from '@/lib/format'
import {
  SLIP_STATUS_LABELS,
  SLIP_TYPE_LABELS,
  type SlipStatus,
} from '@/types/storage'
import { requireFlow } from '@/features/auth/guards'

export const Route = createFileRoute('/_authenticated/storage/$slipId')({
  beforeLoad: requireFlow('storage'),
  component: SlipDetailPage,
})

function SlipDetailPage() {
  const { slipId } = Route.useParams()
  const navigate = useNavigate()
  const { data: slip, isLoading, isError } = useSlip(slipId)
  const { data: boats } = useBoats()
  const assignMutation = useAssignBoat()
  const vacateMutation = useVacateSlip()
  const deleteMutation = useDeleteSlip()
  const { data: activeAllocations } = useAllocations(true)
  const rateMutation = useUpdateAllocationRate()
  const payerMutation = useUpdateAllocationPayer()
  const { data: customers } = useCustomers()
  const [selectedBoatId, setSelectedBoatId] = useState<string>('')
  const [assignRate, setAssignRate] = useState('')
  const [assignCustomerId, setAssignCustomerId] = useState<string>('none')
  const [assignOpen, setAssignOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [vacateOpen, setVacateOpen] = useState(false)
  const [rateEditOpen, setRateEditOpen] = useState(false)
  const [rateEdit, setRateEdit] = useState('')
  const [payerEditOpen, setPayerEditOpen] = useState(false)
  const [payerEdit, setPayerEdit] = useState('none')
  const [actionError, setActionError] = useState<string | null>(null)

  // The active lease for this slip, if occupied (carries the billing rate).
  const activeAllocation = activeAllocations?.find((a) => a.slip_id === slipId)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (isError || !slip) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive">Slip not found.</p>
        <Link to="/storage">
          <Button variant="outline">Back to Storage</Button>
        </Link>
      </div>
    )
  }

  const isOccupied = !!slip.current_boat_id
  // Boats that can never be picked here — surfaced as a hint so the list's
  // gaps are explained instead of silent.
  const oversizedCount = (boats ?? []).filter((b) => b.length_ft > slip.max_length_ft).length

  const handleAssign = async () => {
    if (!selectedBoatId) return
    setActionError(null)
    try {
      await assignMutation.mutateAsync({
        slipId,
        boatId: selectedBoatId,
        monthlyRate: Number(assignRate) > 0 ? Number(assignRate) : undefined,
        customerId: assignCustomerId !== 'none' ? assignCustomerId : undefined,
      })
      setAssignOpen(false)
      setSelectedBoatId('')
      setAssignRate('')
      setAssignCustomerId('none')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to assign boat')
    }
  }

  const handleRateSave = async () => {
    if (!activeAllocation) return
    setActionError(null)
    try {
      await rateMutation.mutateAsync({
        allocationId: activeAllocation.id,
        monthlyRate: Number(rateEdit) > 0 ? Number(rateEdit) : null,
      })
      setRateEditOpen(false)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update rate')
    }
  }

  const handlePayerSave = async () => {
    if (!activeAllocation) return
    setActionError(null)
    try {
      await payerMutation.mutateAsync({
        allocationId: activeAllocation.id,
        customerId: payerEdit !== 'none' ? payerEdit : null,
      })
      setPayerEditOpen(false)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update payer')
    }
  }

  const handleVacate = async () => {
    setActionError(null)
    try {
      await vacateMutation.mutateAsync(slipId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to vacate slip')
    }
  }

  const handleDelete = async () => {
    setActionError(null)
    try {
      await deleteMutation.mutateAsync(slipId)
      navigate({ to: '/storage' })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete slip')
      setDeleteOpen(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/storage">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Storage
        </Button>
      </Link>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="break-words text-3xl font-bold tracking-tight flex items-center gap-2">
            <Anchor className="h-8 w-8 shrink-0" /> Slip {slip.slip_number}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="default" className="text-md">
            {SLIP_STATUS_LABELS[slip.status as SlipStatus]}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" /> Edit
          </Button>
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={isOccupied}>
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete slip {slip.slip_number}?</DialogTitle>
                <DialogDescription>
                  This cannot be undone. The slip will be removed from the storage list and its
                  assignment history stays in Finance.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete Slip'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Slip Specifications</CardTitle>
            <CardDescription>Physical constraints of this storage location.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Anchor className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">{SLIP_TYPE_LABELS[slip.type]} Storage</span>
            </div>
            <div className="flex items-center gap-4">
              <Ruler className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">Max Length: {slip.max_length_ft} ft</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Current Occupant</CardTitle>
            <CardDescription>Boat currently assigned to this slip.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {isOccupied && slip.current_boat_name ? (
              <div className="flex flex-col gap-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <Ship className="h-6 w-6 shrink-0 text-primary" />
                    <span className="truncate font-medium text-lg">{slip.current_boat_name}</span>
                  </div>
                  {/* Cross-module navigation: Storage -> Inventory */}
                  <Link to="/inventory/$boatId" params={{ boatId: slip.current_boat_id! }}>
                    <Button variant="outline" size="sm" className="gap-2">
                      View Boat <ExternalLink className="h-3 w-3" />
                    </Button>
                  </Link>
                </div>
                {activeAllocation && (
                  <>
                    <div className="flex items-center justify-between gap-3 border-t pt-3">
                      <span className="text-sm text-muted-foreground">
                        Monthly rate:{' '}
                        <span className="font-medium text-foreground">
                          {activeAllocation.monthly_rate
                            ? formatMoney(activeAllocation.monthly_rate)
                            : 'not billed'}
                        </span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => {
                          setRateEdit(
                            activeAllocation.monthly_rate
                              ? String(activeAllocation.monthly_rate)
                              : '',
                          )
                          setRateEditOpen(true)
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit rate
                      </Button>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t pt-3">
                      <span className="text-sm text-muted-foreground">
                        Payer:{' '}
                        <span className="font-medium text-foreground">
                          {activeAllocation.customer_name || 'none — no invoices generated'}
                        </span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => {
                          setPayerEdit(activeAllocation.customer_id ?? 'none')
                          setPayerEditOpen(true)
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit payer
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[100px] border-2 border-dashed border-muted-foreground/20 rounded-lg">
                <span className="text-muted-foreground">Slip is empty</span>
              </div>
            )}

            <div className="flex gap-2">
              {!isOccupied && (
                <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      className="gap-2"
                      onClick={() => setActionError(null)}
                    >
                      <UserPlus className="h-4 w-4" /> Assign Boat
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Assign a boat to slip {slip.slip_number}</DialogTitle>
                      <DialogDescription>
                        Pick a boat from inventory. If it's already in another slip, it will be
                        moved here. Boats longer than {slip.max_length_ft} ft cannot be placed in
                        this slip.
                      </DialogDescription>
                    </DialogHeader>
                    <Select value={selectedBoatId} onValueChange={setSelectedBoatId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a boat" />
                      </SelectTrigger>
                      <SelectContent>
                        {boats
                          ?.filter((b) => b.length_ft <= slip.max_length_ft)
                          .map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.make} {b.model} ({b.length_ft} ft)
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {oversizedCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {oversizedCount === 1
                          ? `1 boat longer than ${slip.max_length_ft} ft is hidden — it does not fit this slip.`
                          : `${oversizedCount} boats longer than ${slip.max_length_ft} ft are hidden — they do not fit this slip.`}
                      </p>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground">Paying customer</Label>
                      <Select value={assignCustomerId} onValueChange={setAssignCustomerId}>
                        <SelectTrigger>
                          <SelectValue placeholder="No payer yet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No payer yet</SelectItem>
                          {(customers ?? []).map((c) => {
                            const name =
                              [c.first_name, c.last_name].filter(Boolean).join(' ') ||
                              c.company_name ||
                              c.id
                            return (
                              <SelectItem key={c.id} value={c.id}>
                                {name}
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        The customer invoiced monthly (invoices appear under Finance → Invoices).
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground">Monthly rate ($)</Label>
                      <PriceInput
                        placeholder="Not billed"
                        value={assignRate}
                        onChange={setAssignRate}
                        onBlur={() => {}}
                      />
                      <p className="text-xs text-muted-foreground">
                        Optional. Drives the recurring storage-rental income on the Finance page.
                      </p>
                    </div>
                    {actionError && <p className="text-sm text-destructive">{actionError}</p>}
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogClose>
                      <Button
                        onClick={handleAssign}
                        disabled={!selectedBoatId || assignMutation.isPending}
                      >
                        {assignMutation.isPending ? 'Assigning…' : 'Assign'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              {isOccupied && (
                <Dialog open={vacateOpen} onOpenChange={setVacateOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2" disabled={vacateMutation.isPending}>
                      <UserMinus className="h-4 w-4" />
                      {vacateMutation.isPending ? 'Vacating…' : 'Vacate Slip'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Vacate slip {slip.slip_number}?</DialogTitle>
                      <DialogDescription>
                        {slip.current_boat_name} will be removed from this slip and its monthly
                        billing stops. The boat stays in inventory.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogClose>
                      <Button variant="destructive" onClick={handleVacate} disabled={vacateMutation.isPending}>
                        Vacate Slip
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            {actionError && isOccupied && <p className="text-sm text-destructive">{actionError}</p>}
          </CardContent>
        </Card>
      </div>

      <SlipFormDialog open={editOpen} onOpenChange={setEditOpen} mode="edit" slip={slip} />

      <Dialog open={rateEditOpen} onOpenChange={setRateEditOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Monthly rate — slip {slip.slip_number}</DialogTitle>
            <DialogDescription>
              New rate applies to future months on the Finance page; already-recorded entries stay
              as they are. Leave empty to stop billing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Rate ($/month)</Label>
            <PriceInput
              placeholder="Not billed"
              value={rateEdit}
              onChange={setRateEdit}
              onBlur={() => {}}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleRateSave} disabled={rateMutation.isPending}>
              {rateMutation.isPending ? 'Saving…' : 'Save rate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payerEditOpen} onOpenChange={setPayerEditOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Paying customer — slip {slip.slip_number}</DialogTitle>
            <DialogDescription>
              The customer billed for this slip. Storage invoices generate on the
              daily billing pass once a payer is set (P&amp;L storage fees are
              unaffected).
            </DialogDescription>
          </DialogHeader>
          <Select value={payerEdit} onValueChange={setPayerEdit}>
            <SelectTrigger>
              <SelectValue placeholder="No payer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No payer</SelectItem>
              {(customers ?? []).map((c) => {
                const name =
                  [c.first_name, c.last_name].filter(Boolean).join(' ') ||
                  c.company_name ||
                  c.id
                return (
                  <SelectItem key={c.id} value={c.id}>
                    {name}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handlePayerSave} disabled={payerMutation.isPending}>
              {payerMutation.isPending ? 'Saving…' : 'Save payer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
